// Lazy load icons to avoid loading them on auth routes
import { lazy, Suspense, FC } from "react";

const LogoIcon = lazy(() => import("@/assets/icons/logo_white.svg?react"));
const LogoutIcon = lazy(() => import("@/assets/icons-mono/logout.svg?react"));
import useCustomTranslation from "@/localization/useCustomTranslation";
import { cn, Separator } from "@fido/ui-lib";
import packageJson from "../../../package.json";
import Wave from "../Wave/Wave";
import SidebarNav from "./Components/SidebarNav";
import useLogout from "./useLogout";
import { NavLink } from "react-router";

interface SidebarProps {}

const Sidebar: FC<SidebarProps> = () => {
  const onLogout = useLogout();
  const { prefixedT } = useCustomTranslation("SIDEBAR");

  return (
    <div
      className={cn(
        // Desktop: fixed sidebar, width depends on open/collapsed
        "fixed h-full z-10 transition-all duration-300 group ",
        // Mobile: full screen overlay, slide in/out
        "md:min-w-[250px] md:w-[250px] md:fixed md:translate-x-0 md:h-screen md:p-0",
        "-translate-x-full md:translate-x-0",
        "transition-transform"
      )}
      style={{
        // Ensure transform transition for mobile slide
        transitionProperty: "width, height, min-width, transform",
        transitionDuration: "300ms",
        transitionTimingFunction: "ease"
      }}
    >
      <div
        className={cn(
          "static flex flex-col h-full justify-start bg-(--dark-blue) w-full overflow-hidden transition-all duration-300 z-[999]",
          "overflow-y-auto",
          "md:absolute md:top-0 md:left-0 md:h-full md:w-[250px]"
        )}
        data-testid="sidebar"
      >
        <Wave />

        <div className="flex items-center justify-start pl-7 relative h-28">
          <NavLink to="/app/home">
            <Suspense
              fallback={
                <div className="w-24 h-10 bg-white/20 rounded animate-pulse" />
              }
            >
              <LogoIcon
                className={cn(
                  "sidebar__logo transition-all duration-300 w-24 h-10"
                )}
              />
            </Suspense>
          </NavLink>
        </div>

        <div className="space-y-8 z-10">
          <SidebarNav />
          <div className="flex justify-center w-full">
            <Separator
              decorative
              className="w-4/5 rounded-md"
            />
          </div>
          <div
            onClick={onLogout}
            data-testid="logoutAction"
            className="h-10 pl-7 pr-5 cursor-pointer flex items-center gap-2 font-medium text-xs text-white hover:text-(--dark-blue) hover:bg-white transition-colors duration-300"
          >
            <Suspense
              fallback={
                <div className="h-5 w-5 bg-white/20 rounded animate-pulse" />
              }
            >
              <LogoutIcon className="h-5 w-5 shrink-0" />
            </Suspense>
            <span className="truncate">{prefixedT("LOGOUT")}</span>
          </div>
        </div>

        <div className="mb-10 whitespace-nowrap mt-auto">
          <p className="text-[10px] pl-4 text-white">
            {"Version " + packageJson.version}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
